import React from 'react';
import type { CandidateProfileDetail } from '../../lib/engagementService';
import { boostRealUser, boostCandidateProfileViews } from '../../lib/engagementService';
import type { SeedCampaign } from '../../lib/seedService';
import BoostPanel from './BoostPanel';

interface Props {
  detail: CandidateProfileDetail;
  campaigns: SeedCampaign[];
  onBoostComplete: (label: string, campaign: string, count: number) => void;
}

const CandidateCard: React.FC<Props> = ({ detail, campaigns, onBoostComplete }) => {
  const { firstName, lastName, city, email, isSeed, seedTag, photoUrl, videos, stats, applications } = detail;
  const fullName = `${firstName} ${lastName}`.trim() || 'Sans nom';

  return (
    <div className="bg-white rounded-xl border-2 border-blue-200 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg">
            {(firstName?.[0] || '?').toUpperCase()}
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{fullName}</h2>
            {isSeed && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">SEED</span>}
          </div>
          <p className="text-sm text-gray-500">{city} &middot; {email}</p>
          {seedTag && <p className="text-xs text-gray-400">Campagne : {seedTag}</p>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        {([
          { label: 'Likes reçus', value: stats.totalLikes, color: 'text-pink-600 bg-pink-50' },
          { label: 'Vues reçues', value: stats.totalViews, color: 'text-blue-600 bg-blue-50' },
          { label: 'Vues profil', value: stats.profileViewsReceived, color: 'text-purple-600 bg-purple-50' },
          { label: 'Candidatures', value: stats.applicationsCount, color: 'text-green-600 bg-green-50' },
          { label: 'Favoris reçus', value: stats.favoritesReceived, color: 'text-amber-600 bg-amber-50' },
        ] as const).map((s) => (
          <div key={s.label} className={`rounded-lg p-3 ${s.color}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Videos */}
      {videos.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Vidéos ({videos.length})</h3>
          <div className="border border-gray-100 rounded-lg overflow-hidden divide-y divide-gray-100">
            {videos.map((v) => (
              <div key={v.id} className="px-3 py-2 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded ${v.status === 'approved' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {v.status}
                  </span>
                  <span className="text-gray-400">{v.duration}s</span>
                  <span className="text-gray-400">{v.createdAt}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-pink-600 font-medium">{v.likes} likes</span>
                  <span className="text-blue-600 font-medium">{v.views} vues</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Boost Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Booster ce candidat</h3>
        <div className="space-y-2">
          <BoostPanel
            campaigns={campaigns}
            label="+ Likes vidéos"
            onBoost={(c, n, s) =>
              boostRealUser({ campaignTag: c, targetUserId: detail.userId, interaction: 'like', count: n, spreadDays: s })
            }
            onComplete={(c, n) => onBoostComplete(`Likes ${fullName}`, c, n)}
          />
          <BoostPanel
            campaigns={campaigns}
            label="+ Vues vidéos"
            onBoost={(c, n, s) =>
              boostRealUser({ campaignTag: c, targetUserId: detail.userId, interaction: 'view', count: n, spreadDays: s })
            }
            onComplete={(c, n) => onBoostComplete(`Vues ${fullName}`, c, n)}
          />
          <BoostPanel
            campaigns={campaigns}
            label="+ Vues profil"
            onBoost={(c, n, s) =>
              boostCandidateProfileViews({ campaignTag: c, candidateProfileId: detail.candidateProfileId, count: n, spreadDays: s })
            }
            onComplete={(c, n) => onBoostComplete(`Vues profil ${fullName}`, c, n)}
          />
        </div>
      </div>

      {/* Applications */}
      {applications.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Candidatures ({applications.length})</h3>
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            {applications.map((a) => (
              <div key={a.id} className="px-3 py-2 flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700">{a.offerTitle}</span>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded ${
                    a.status === 'selected' ? 'bg-green-50 text-green-700' :
                    a.status === 'rejected' ? 'bg-red-50 text-red-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {a.status}
                  </span>
                  <span className="text-gray-400">{a.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateCard;
